import {
  Component,
  ElementRef,
  Input,
  OnInit,
  Self,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { CustomPaggerService } from 'projects/Autofacets-Core/src/app/common-services/custom-pagger.service';
import { Utility } from 'projects/Autofacets-Core/src/app/utils/utility';
import { Observable, Subscription } from 'rxjs';
import * as XLSX from 'xlsx';
import { SelectListItemViewModel } from '../../../../business-entities/select-list-item-view-model';
import { DealerBusinessService } from '../../../../business-services/dealer.service';
import * as Enums from 'projects/Autofacets-Core/src/app/utils/enums';
import { Constant } from 'projects/Autofacets-Core/src/app/utils/constant';
import { CultureLocaleService } from '../../../../services/culture-locale.service';
import { UserDealersModel } from '../../../../business-entities/dealer-details.model';
import { CountryISO } from 'ngx-intl-tel-input-gg';
import { SettingService } from 'projects/Administration/src/app/business-services/setting.service';

@Component({
  selector: 'app-add-campaign-customer',
  templateUrl: './add-campaign-customer.component.html',
  styleUrls: ['./add-campaign-customer.component.css'],
  providers: [CustomPaggerService],
})
export class AddCampaignCustomerComponent implements OnInit {
  @Input() actionFlag: any;
  @ViewChild('inputFile') inputFile: ElementRef;
  dealerList: Array<SelectListItemViewModel>;
  isExcelFile: boolean;
  addCustomerForm;
  ImportedRecipients = [];
  excelData = [];
  paginationSub: Subscription;
  total$: Observable<number>;
  selectedDealer = '';
  requiredFields = [
    'CustomerName',
    'Email',
    'Mobile',
    'DealerName',
    'PostalCode',
    'Address',
    'City',
    'LicensePlateNo',
  ];
  validFormat: boolean = true;
  CountryISO = CountryISO;
  isInitialLoad: boolean= true;
  onlyCountries: string[] = [];
  countryDialCodes: any[]= [];
  selectedCountryISO= this.cultureLocaleService.getLocale() || CountryISO.Norway;
  
  constructor(
    private readonly activeModal: NgbActiveModal,
    public dealerBusinessService: DealerBusinessService,
    private readonly formBuilder: FormBuilder,
    @Self() public pService: CustomPaggerService,
    private readonly translate: TranslateService,
    private readonly spinner: NgxSpinnerService,
    private readonly toastr: ToastrService,
    private readonly cultureLocaleService: CultureLocaleService,
    private readonly settingService: SettingService
  ) {
    this.bindModel();
  }

  bindModel() {
    this.onlyCountries = this.settingService.onlyCountries;
    this.countryDialCodes = this.settingService.countryDialCodes;
    this.addCustomerForm = this.formBuilder.group({
      CustomerName: new FormControl('', [Validators.required]),
      Email: new FormControl('', [
        Validators.required,
        Validators.pattern(Enums.RegexKeyCollection.Email),
      ]),
      MobileCode: '',
      Mobile: new FormControl('', [
        Validators.required
      ]),
      PostalCode: '',
      City: '',
      Address: '',
      DealerId: '',
      LicensePlateNo: '',
      Flag: 'Added',
      EmailConsent: true,
      SMSConsent: true,
    });
  }

  isDigit(event): void {
    const isDigit = Utility.isDigit(event);
    if (!isDigit) {
      event.preventDefault();
    }
  }

  onDealerSelected(event) {
    this.addCustomerForm.patchValue({ DealerId: event });
  }

  onCountryChange(obj) {
    if(this.isInitialLoad && this.addCustomerForm.get('MobileCode').value){
      this.telInputObject();
      this.isInitialLoad= false;
    } else {
      if(this.isInitialLoad){
        this.telInputObject();
        this.addCustomerForm.patchValue({ MobileCode: obj.dialCode });
        this.isInitialLoad= false;
      } else {
        this.addCustomerForm.patchValue({ MobileCode: obj.dialCode });
      }
    }
  }

  telInputObject() {
      let mobileCode = this.addCustomerForm.get('MobileCode').value;
      if (mobileCode) {
        this.addCustomerForm.patchValue({
          MobileCode: mobileCode.replace('+', ''),
        });
        let countryCode = this.getCountryCodeByDialCode(mobileCode);
        this.selectedCountryISO=
          countryCode ? countryCode : this.cultureLocaleService.getLocale();
      } else {
        this.selectedCountryISO= this.cultureLocaleService.getLocale();
      }
  }

  getCountryCodeByDialCode(dialCode: any) {
    const country = this.countryDialCodes.find(c => c.dialCode === dialCode);
    return country ? country.countryCode : null;
  }

  get cpf() {
    return this.addCustomerForm.controls;
  }

  ngOnInit(): void {
    this.getUserDealers();
  }

  async getUserDealers() {
    this.spinner.show();
    await this.dealerBusinessService
      .getUserDealersDropdown()
      .then((response) => {
        this.dealerList = this.mapDealer(response.userDealersModel);
      })
      .finally(() => this.spinner.hide());
  }

  mapDealer(array: UserDealersModel[]) {
    let supperArray = [];
    if (array && array.length > 0) {
      array.forEach((element) => {
        let single = Object();
        single.value = element.dealerId;
        single.text = element.dealerName;
        supperArray.push(single);
      });
    }
    return supperArray;
  }
  onFileChange(evt) {
    const target: DataTransfer = <DataTransfer>evt.target;
    const fileExtensionRegex = /\.(xls|xlsx)$/i;
    this.isExcelFile = !!fileExtensionRegex.exec(target.files[0].name);
    if (target.files.length > 1) {
      this.inputFile.nativeElement.value = '';
    }
    if (this.isExcelFile) {
      this.spinner.show();
      const reader: FileReader = new FileReader();
      reader.onload = (e: any) => {
        /* read workbook */
        const bstr: string = e.target.result;
        const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

        /* grab first sheet */
        const wsname: string = wb.SheetNames[0];
        const ws: XLSX.WorkSheet = wb.Sheets[wsname];

        /* save data */
        this.excelData = XLSX.utils.sheet_to_json(ws);
      };

      reader.readAsBinaryString(target.files[0]);

      reader.onloadend = (_e) => {
        this.validFormat = true;
        this.ImportedRecipients = [];
        this.spinner.hide();
        let keys = Object.keys(this.excelData[0]);

        keys.forEach((x) => {
          if (!this.requiredFields.find((y) => y == x))
            this.validFormat = false;
        });

        this.excelData.forEach((x) => {
          x.valid =
            Object.keys(x).find((y) => y == 'CustomerName') &&
              Object.keys(x).find((y) => y == 'Email') &&
              Object.keys(x).find((y) => y == 'Mobile')
              ? true
              : false;
        });

        if (this.validFormat) {
          this.pService.dataSource = this.excelData;
          this.paginationSub = this.pService.dealers$.subscribe((result) => {
            this.ImportedRecipients = result;
          });
          this.total$ = this.pService.total$;
        }
      };
    } else {
      this.inputFile.nativeElement.value = '';
    }
  }

  onSubmit() {
    if (this.addCustomerForm.status === 'VALID') {
      this.addCustomerForm.controls.Mobile= this.addCustomerForm.controls.Mobile.value?.number ? this.addCustomerForm.controls.Mobile.value.number.toString().replace(/\s+/g, '') : this.addCustomerForm.controls.Mobile.value;
      this.activeModal.close({
        success: true,
        data: [this.addCustomerForm.value],
      });
      this.toastr.success(this.translate.instant('lblAddCustomerMsg'));
    }
  }

  excelDataSave() {
    this.excelData = this.excelData.filter((x) => {
      return Object.keys(x).find((y) => y == 'CustomerName') &&
        Object.keys(x).find((y) => y == 'Email') &&
        Object.keys(x).find((y) => y == 'Mobile')
        ? true
        : false;
    });

    this.excelData.forEach((x) => {
      x.Flag = 'Imported';
      delete x.valid;
    });

    this.activeModal.close({
      success: true,
      data: this.excelData,
    });
    this.toastr.success(this.translate.instant('lblImportCustomerMsg'));
  }

  OnClose() {
    this.activeModal.close();
  }
}
